import React, { useState } from 'react';
import OnboardingOptionCard from './OnboardingOptionCard';
import './OnboardingOptionCard.css';
import './OnboardingSummaryCard.css';

// Read-only confirmation screen shown after the final onboarding answer,
// matching docs/design/ivori-chat-prototype.html's summary-grid (buildSummaryGrid), plus
// an edit mode matching the prototype's mountConfirm('edit', ...) treatment.
//
// By the time this card first renders, onboarding_completed_at is already
// set server-side (save_answer sets it on the 'pathways' step itself, the
// true final answer). "Set up my workspace" is a pure client-side
// transition — stop showing this card, unlock the composer/tabs. Editing an
// answer from here, however, is a real save: it goes through the same
// POST /api/onboarding/answers endpoint every other step uses (save_answer's
// own docstring is explicit that it never refuses because onboarding is
// already complete — editing after completion reuses it unchanged).
//
// answers shape: { firstNameEdit?, surnameEdit?, [stepId]: value } — the
// saved answers, single-select/text as a string, multi-select as a list.
// stepsById: { [stepId]: step } — the served step catalogue, in catalogue
// order (the caller builds it with Object.fromEntries over the served steps
// list, and string keys keep their insertion order). It decides which rows
// the card shows and in what order, resolves option codes to display labels
// (mirrors labelFor() in the prototype), and supplies the real step
// definitions edit mode renders via OnboardingOptionCard.
// signupName: { first, last } — fallback display name when firstNameEdit/
// surnameEdit were never answered (the confirm step was answered 'yes').
// onFinish: saves the summary step's acknowledgement, completing
// onboarding (unchanged) — only rendered pre-completion; see `completed`.
// onSaveEdit(stepId, value): persists one changed step's new value. Caller
// (ChatAgentPanel) is responsible for the actual POST + state refetch,
// exactly like it already does for handleOnboardingAnswer.
// completed: true once onboarding_completed_at is set. This card is pinned
// permanently below the transcript once true (ChatAgentPanel renders it
// regardless of onboardingNextStep in that state) — the onboarding session
// becomes a permanent, editable settings surface rather than a
// conversation. "Set up my workspace" only makes sense pre-completion, so
// it's hidden once completed; "Change answers" stays available forever.
//
// Rows: the Name row first, then one row per catalogue step that is active
// given the answers and is a single, multi or text step outside the name
// pair. The summary step itself never gets a row.

// The name pair is shown as the one Name row (edited name, else the signup
// name), never as rows of its own, whatever the confirm step says: a name
// edited here saves only the edit step (edit_of its confirm step).
const NAME_STEP_IDS = new Set(['firstName', 'firstNameEdit', 'surname', 'surnameEdit']);
const ROW_KINDS = new Set(['single', 'multi', 'text']);

// Same semantics as ChatAgentPanel.js's isOnboardingStepActive and the
// backend's is_step_active: a step with `when` is active only if the named
// step's answer equals the given value.
function isStepActive(step, answers) {
  if (!step.when) return true;
  return answers[step.when.step] === step.when.equals;
}

// Every catalogue step that can have a row, in catalogue order.
function rowableSteps(stepsById) {
  return Object.values(stepsById || {}).filter(
    (step) => step && ROW_KINDS.has(step.kind) && !NAME_STEP_IDS.has(step.id)
  );
}

function rowStepsFor(stepsById, answers) {
  return rowableSteps(stepsById).filter((step) => isStepActive(step, answers));
}

// A readable form of an option code the step does not list: 'english_lit'
// -> 'English lit'. Anything that is not a string, number or boolean has no
// readable form and becomes ''. Row titles never come from here: every served
// step carries its summary_label (onboarding_steps.validate_registry).
function humanise(value) {
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value !== 'string') return '';
  const words = value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[\s_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

function labelFor(step, code) {
  if (typeof code === 'string' || typeof code === 'number') {
    const opt = (step?.options || []).find((o) => String(o.v) === String(code));
    if (opt) return opt.l;
  }
  return humanise(code);
}

function answerList(value) {
  if (Array.isArray(value)) return value;
  return value === undefined || value === null || value === '' ? [] : [value];
}

// A text answer is the user's own words: shown as typed, not humanised.
function textValue(value) {
  return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
}

function chipsFor(step, value) {
  const toChip = step.kind === 'text' ? textValue : (code) => labelFor(step, code);
  return answerList(value).map(toChip).filter(Boolean);
}

function initialDraftFor(step, value) {
  if (step.kind === 'multi') return Array.isArray(value) ? value : [];
  if (step.kind === 'text') return typeof value === 'string' ? value : '';
  return value === undefined ? null : value;
}

// The value to save for a row, or undefined when there is nothing to save:
// the draft is unchanged, or is not a value save_answer would accept (no
// single choice, an empty selection, blank text).
function draftToSave(step, draft, original) {
  if (step.kind === 'multi') {
    const next = Array.isArray(draft) ? draft : [];
    const prev = Array.isArray(original) ? original : [];
    const changed = JSON.stringify([...next].sort()) !== JSON.stringify([...prev].sort());
    return changed && next.length > 0 ? next : undefined;
  }
  if (step.kind === 'text') {
    const next = typeof draft === 'string' ? draft.trim() : '';
    return next && next !== original ? next : undefined;
  }
  return draft && draft !== original ? draft : undefined;
}

function SummaryRow({ label, chips }) {
  return (
    <div className="onboarding-summary-row">
      <div className="onboarding-eyebrow">{label}</div>
      <div className="onboarding-chips">
        {chips.length === 0 ? (
          <span className="onboarding-chip">Not set</span>
        ) : (
          chips.map((text, i) => (
            <span key={i} className="onboarding-chip">{text}</span>
          ))
        )}
      </div>
    </div>
  );
}

function OnboardingSummaryCard({ answers, stepsById, signupName, onFinish, onSaveEdit, completed }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [firstNameDraft, setFirstNameDraft] = useState('');
  const [surnameDraft, setSurnameDraft] = useState('');
  const [drafts, setDrafts] = useState({});

  const saved = answers || {};
  const displayFirst = saved.firstNameEdit || signupName?.first || '';
  const displaySurname = saved.surnameEdit || signupName?.last || '';
  const displayName = [displayFirst, displaySurname].filter(Boolean).join(' ');

  const startEditing = () => {
    setFirstNameDraft(displayFirst);
    setSurnameDraft(displaySurname);
    // Every row step in the catalogue gets a draft, active or not, so a row
    // a draft answer brings into view starts from its saved value.
    setDrafts(Object.fromEntries(
      rowableSteps(stepsById).map((step) => [step.id, initialDraftFor(step, saved[step.id])])
    ));
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
  };

  const setDraft = (stepId, value) => {
    setDrafts((prev) => ({ ...prev, [stepId]: value }));
  };

  // While editing, which rows are active follows the drafts, so changing a
  // step another depends on shows or hides the dependent row at once.
  const editingAnswers = { ...saved, ...drafts };

  const saveChanges = async () => {
    setSaving(true);
    try {
      // firstNameEdit/surnameEdit are edits of their confirm step (edit_of
      // in the catalogue): the server accepts them whatever the confirm step
      // says, and an edit adds nothing to the transcript.
      if (firstNameDraft.trim() && firstNameDraft.trim() !== displayFirst) {
        await onSaveEdit('firstNameEdit', firstNameDraft.trim());
      }
      if (surnameDraft.trim() && surnameDraft.trim() !== displaySurname) {
        await onSaveEdit('surnameEdit', surnameDraft.trim());
      }
      // Catalogue order, so a step another depends on is saved first.
      for (const step of rowStepsFor(stepsById, editingAnswers)) {
        const value = draftToSave(step, drafts[step.id], saved[step.id]);
        if (value !== undefined) {
          await onSaveEdit(step.id, value);
        }
      }
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="onboarding-card onboarding-summary-card onboarding-summary-card-editing">
        <div className="onboarding-summary-grid">
          <div className="onboarding-summary-row">
            <div className="onboarding-eyebrow">Name</div>
            <div className="onboarding-name-fields">
              <label className="onboarding-name-field">
                <span>First name</span>
                <input
                  type="text"
                  value={firstNameDraft}
                  onChange={(e) => setFirstNameDraft(e.target.value)}
                  disabled={saving}
                />
              </label>
              <label className="onboarding-name-field">
                <span>Surname</span>
                <input
                  type="text"
                  value={surnameDraft}
                  onChange={(e) => setSurnameDraft(e.target.value)}
                  disabled={saving}
                />
              </label>
            </div>
          </div>

          {rowStepsFor(stepsById, editingAnswers).map((step) => {
            const title = step.summary_label;
            if (step.kind === 'text') {
              return (
                <div className="onboarding-summary-row" key={step.id}>
                  <div className="onboarding-eyebrow">{title}</div>
                  <label className="onboarding-name-field">
                    <span>{step.placeholder || title}</span>
                    <input
                      type="text"
                      value={drafts[step.id] ?? ''}
                      onChange={(e) => setDraft(step.id, e.target.value)}
                      disabled={saving}
                    />
                  </label>
                </div>
              );
            }
            return (
              <div className="onboarding-summary-row" key={step.id}>
                <div className="onboarding-eyebrow">{title}</div>
                <OnboardingOptionCard
                  step={{ ...step, options: step.options || [] }}
                  stepIndex={0}
                  totalSteps={1}
                  initialValue={drafts[step.id]}
                  onAnswer={(value) => setDraft(step.id, value)}
                  onSelectionChange={(codes) => setDraft(step.id, codes)}
                />
              </div>
            );
          })}
        </div>
        <div className="onboarding-card-foot onboarding-card-foot-edit">
          <button
            type="button"
            className="onboarding-btn-quiet"
            onClick={cancelEditing}
            disabled={saving}
          >
            Discard changes
          </button>
          <button
            type="button"
            className="onboarding-btn-primary"
            onClick={saveChanges}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save answers'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-card onboarding-summary-card">
      <div className="onboarding-summary-grid">
        <SummaryRow label="Name" chips={displayName ? [displayName] : []} />
        {rowStepsFor(stepsById, saved).map((step) => (
          <SummaryRow key={step.id} label={step.summary_label} chips={chipsFor(step, saved[step.id])} />
        ))}
      </div>
      <div className="onboarding-card-foot onboarding-card-foot-readonly">
        <button type="button" className="onboarding-btn-quiet" onClick={startEditing}>
          Change answers
        </button>
        {!completed && (
          <button type="button" className="onboarding-btn-primary" onClick={onFinish}>
            Set up my workspace
          </button>
        )}
      </div>
    </div>
  );
}

export default OnboardingSummaryCard;
